import React from 'react';

import Carousel from 'nuka-carousel';
import styled from 'styled-components';

import { IMainMenuSectionView, IMainMenuViewOption } from '@rbi-ctg/menu';
import { MAX_CAROUSEL_WIDTH } from 'components/main-menu-carousel/base';
import { MenuCardButton } from 'components/menu-card-button';
import { useMediaQuery } from 'hooks/use-media-query';
import { useWindowDimensions } from 'hooks/use-window-dimensions';
import { excludeDisabledMenuSections } from 'pages/main-menu/utils';
import { useDayPartContext } from 'state/day-part';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { routes } from 'utils/routing';

import { SHOW_MAIN_MENU_CAROUSEL_HEADER } from './constants';
import { MainMenuCarouselHeader } from './main-menu-carousel-header';
import { MainMenuCarouselTile } from './main-menu-carousel-tile';
import { CarouselContainer } from './styles';
import { theme } from './theme';

// #region styles
const minimumSlideWidth = 160;

const CarouselForward = styled(MenuCardButton)`
  transition: 0.3s;
  background-color: ${({ disabled, theme: { token } }) =>
    disabled ? token('background-disabled') : token('background-button-primary-default')};
  position: relative;
  inset-block-end: 50%;
  inset-inline-start: 50px;

  &:hover {
    background-color: ${({ disabled, theme: { token } }) =>
      disabled ? token('background-disabled') : token('background-button-primary-default')};
  }
`;

const CarouselBackward = styled(CarouselForward)`
  transform: rotate(180deg);
  inset-inline-start: -50px;

  &:active {
    transform: scale(0.95) rotate(180deg);
  }
`;
// #endregion

const MenuOptionCarousel = () => {
  const { activeDayParts } = useDayPartContext();
  const { showStaticMenu, isStaticMenu } = useMenuContext();
  const { serviceMode } = useServiceModeContext();
  const { getMainMenu, getPricingAndAvailability } = useMainMenuContext();
  const enableBottomServiceMode = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const mainMenu = getMainMenu();
  const { width: measuredWidth } = useWindowDimensions();
  const { data, loading } = mainMenu;
  const innerWidth = Math.min(measuredWidth, MAX_CAROUSEL_WIDTH);
  const isMobile = useMediaQuery('mobile');

  if (loading || !data || isMobile) {
    return null;
  }

  const sections = (excludeDisabledMenuSections({
    menuData: data?.options?.filter(item => {
      if (isStaticMenu) {
        return true;
      }
      // @ts-ignore
      return getPricingAndAvailability(item?._id)?.isAvailable;
    }) as IMainMenuViewOption[],
    activeDayParts,
    showStaticMenu,
    serviceMode,
  }) || []) as IMainMenuSectionView[];

  // Take account of the carousel link to the main menu (MainMenuCarouselHeader)
  const totalOfSlides = sections.length + (SHOW_MAIN_MENU_CAROUSEL_HEADER ? 1 : 0);
  // when all slides width is less than innerWidth: hide controls and show all slides
  const isLessThanScreenWidth = minimumSlideWidth * totalOfSlides < innerWidth;
  const slidesToShow = isLessThanScreenWidth
    ? totalOfSlides
    : Math.floor(innerWidth / minimumSlideWidth);

  // Add 32px for aesthetics when BSM is on
  const topInset = enableBottomServiceMode ? 32 : theme.extraCarouselInsetWithoutBottomServiceMode;

  return (
    <CarouselContainer
      role={'navigation'}
      aria-label={'menu-category-links'}
      data-testid="menu-option-carousel-container"
      $topInset={topInset}
    >
      <Carousel
        renderBottomCenterControls={null}
        slidesToScroll="auto"
        slidesToShow={slidesToShow}
        withoutControls={isLessThanScreenWidth}
        renderCenterLeftControls={({ previousSlide, currentSlide }) => (
          <CarouselBackward onClick={previousSlide} disabled={currentSlide === 0} />
        )}
        renderCenterRightControls={({ nextSlide, slideCount, currentSlide }) => (
          <CarouselForward
            onClick={nextSlide}
            disabled={slideCount - slidesToShow <= currentSlide}
          />
        )}
      >
        {SHOW_MAIN_MENU_CAROUSEL_HEADER && <MainMenuCarouselHeader to={routes.menu} />}

        {sections.map(section => (
          <MainMenuCarouselTile section={section} key={section._id} aria-labelledby={section._id} />
        ))}
      </Carousel>
    </CarouselContainer>
  );
};

export const MainMenuCarousel = React.memo(MenuOptionCarousel);
